import { EventIngestionPayload, EventPayload, GemSalesConfig, ShopperContext } from "./types";
import { applyIntentSignals } from "./intent";
import { appendBufferedEvent } from "./storage";

const FLUSH_DELAY_MS = 250;

export class GemSalesTracker {
  private pendingEvents: EventPayload[] = [];
  private flushTimer: number | null = null;

  constructor(
    private readonly config: GemSalesConfig,
    private readonly context: ShopperContext,
  ) {}

  track(eventName: string, properties: Record<string, unknown> = {}): void {
    const normalizedEventName = normalizeGemEventName(eventName);
    const timestamp = new Date().toISOString();
    const payload: EventPayload = {
      eventName: normalizedEventName,
      shopId: this.config.shopId,
      anonymousId: this.context.anonymousId,
      sessionId: this.context.sessionId,
      timestamp,
      page: this.context.page,
      properties,
    };

    const intentUpdates = applyIntentSignals(this.context, eventName, properties, timestamp);
    const payloads = [
      payload,
      ...intentUpdates.map<EventPayload>((update) => ({
        ...payload,
        eventName: "intent.updated",
        properties: { ...update },
      })),
    ];

    payloads.forEach((candidate) => this.record(candidate));

    if (!this.config.endpoint) {
      payloads.forEach((candidate) => console.debug("[GemSales]", candidate));
      return;
    }

    this.queue(payloads);
  }

  flush(): void {
    if (!this.config.endpoint || !this.pendingEvents.length) return;

    const events = this.pendingEvents.splice(0);
    const payload = buildIngestionPayload(events);
    this.emit(payload, events);
  }

  private record(payload: EventPayload): void {
    appendBufferedEvent(payload);
  }

  private queue(payloads: EventPayload[]): void {
    this.pendingEvents.push(...payloads);
    if (this.flushTimer !== null) return;

    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, FLUSH_DELAY_MS);
  }

  private emit(payload: EventIngestionPayload, originalEvents: EventPayload[]): void {
    const body = JSON.stringify(payload);
    const sentByBeacon = navigator.sendBeacon?.(this.config.endpoint!, body);
    if (sentByBeacon) return;

    void (
      fetch(this.config.endpoint!, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        this.pendingEvents.unshift(...originalEvents);
      })
    );
  }
}

export function buildIngestionPayload(events: EventPayload[]): EventIngestionPayload {
  return {
    schemaVersion: "gemsales.events.v1",
    sentAt: new Date().toISOString(),
    shopId: events[0]?.shopId ?? "unknown",
    sessionId: events[0]?.sessionId ?? "unknown",
    events,
  };
}

function normalizeGemEventName(eventName: string): string {
  const eventMap: Record<string, string> = {
    "gemsales.trigger.impression": "gemsales_slot_impression",
    "gemsales.trigger.click": "gemsales_opened",
    "gemsales.flow.start": "gemsales_quiz_started",
    "gemsales.flow.answer": "gemsales_answer_submitted",
    "gemsales.flow.complete": "gemsales_recommendation_generated",
    "gemsales.recommendation.view": "gemsales_recommendation_viewed",
    "gemsales.recommendation.click": "gemsales_product_clicked",
    "gemsales.experience.view": "gemsales_experience_viewed",
    "gemsales.experience.section.click": "gemsales_experience_section_clicked",
    "gemsales.experience.guide.click": "gemsales_experience_guide_clicked",
    "gemsales.add_to_cart.assist": "gemsales_add_to_cart_clicked",
    "gemsales.quick_reply.click": "gemsales_quick_reply_clicked",
    "gemsales.followup.submit": "gemsales_followup_submitted",
    "gemsales.followup.answer": "gemsales_followup_answered",
    // Generative surface funnel (P3·3 attribution). The surface CTA is an add-to-cart action,
    // so `cta_click` IS the surface-assisted add-to-cart — the conversion signal an order can
    // later attribute to a surfaceId. Normalizing these here is what lets buildPilotSummary count
    // the funnel; without it the dot-names pass through and no metric ever sees them.
    "gemsales.surface.opened": "gemsales_surface_opened",
    "gemsales.surface.rendered": "gemsales_surface_rendered",
    "gemsales.surface.cta_click": "gemsales_surface_add_to_cart",
    "gemsales.surface.followup": "gemsales_surface_followup",
    "gemsales.surface.closed": "gemsales_surface_closed",
    "gemsales.surface.failed": "gemsales_surface_failed",
    "gemsales.surface.chips_mounted": "gemsales_surface_chips_mounted",
  };

  return eventMap[eventName] ?? eventName;
}
